/**
 * User Detail Page
 * Create/Edit user form
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    ArrowLeft,
    Save,
    Trash2,
    User as UserIcon,
    Mail,
    Lock,
    Shield,
    MapPin,
    Loader2,
} from 'lucide-react';
import { useUser, useCreateUser, useUpdateUser, useDeleteUser } from '@/hooks/useUsers';
import { useRoles } from '@/hooks/access-control';
import { getUserDisplayName, getUserInitials, getStatusColor, type CreateUserPayload } from '@/api/users';
import { toast } from 'sonner';

type UserStatus = 'active' | 'invited' | 'draft' | 'suspended' | 'archived';

interface UserFormData {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    role: string;
    title: string;
    description: string;
    location: string;
    status: UserStatus;
    email_notifications: boolean;
}

const initialFormData: UserFormData = {
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: '',
    title: '',
    description: '',
    location: '',
    status: 'active',
    email_notifications: true,
};

const statusOptions: { value: UserStatus; label: string }[] = [
    { value: 'active', label: 'Active' },
    { value: 'invited', label: 'Invited' },
    { value: 'draft', label: 'Draft' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'archived', label: 'Archived' },
];

export function UserDetailPage() {
    const navigate = useNavigate();
    const { userId } = useParams<{ userId?: string }>();
    const isNew = !userId || userId === 'new';

    // Form state
    const [formData, setFormData] = useState<UserFormData>(initialFormData);
    const [hasChanges, setHasChanges] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    // Queries
    const { data: user, isLoading: userLoading } = useUser(userId || '', {
        fields: ['*'],
    });
    const { data: allRoles = [], isLoading: rolesLoading } = useRoles();

    // Filter out Public role - users can't be assigned to Public role
    // Public role is for unauthenticated access only
    const roles = allRoles.filter(role => role.name.toLowerCase() !== 'public');

    // Mutations
    const createMutation = useCreateUser();
    const updateMutation = useUpdateUser();
    const deleteMutation = useDeleteUser();

    // Populate form when user loads
    useEffect(() => {
        if (user && !isNew) {
            setFormData({
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                email: user.email || '',
                password: '',
                role: typeof user.role === 'string' ? user.role : user.role?.id || '',
                title: user.title || '',
                description: user.description || '',
                location: user.location || '',
                status: (user.status as UserStatus) || 'active',
                email_notifications: user.email_notifications ?? true,
            });
        }
    }, [user, isNew]);

    // Handle form changes
    const handleChange = (field: keyof UserFormData, value: string | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setHasChanges(true);
    };

    // Save handler
    const handleSave = async () => {
        // Validation
        if (!formData.email) {
            toast.error('Email is required');
            return;
        }
        if (isNew && !formData.password) {
            toast.error('Password is required for new users');
            return;
        }
        // Role is optional - users without role get public permissions

        try {
            const payload: CreateUserPayload = {
                first_name: formData.first_name || undefined,
                last_name: formData.last_name || undefined,
                email: formData.email,
                role: formData.role || null,  // null means public access only
                title: formData.title || undefined,
                description: formData.description || undefined,
                location: formData.location || undefined,
                status: formData.status,
            };

            // Only include password if set
            if (formData.password) {
                payload.password = formData.password;
            }

            if (isNew) {
                const newUser = await createMutation.mutateAsync(payload);
                navigate(`/admin/users/${newUser.id}`, { replace: true });
            } else {
                await updateMutation.mutateAsync({ id: userId!, payload });
            }

            setHasChanges(false);
        } catch (error) {
            // Error handled by mutation
        }
    };

    // Delete handler
    const handleDelete = async (e: React.MouseEvent) => {
        e.preventDefault(); // Prevent dialog from closing automatically
        if (!userId) return;

        try {
            await deleteMutation.mutateAsync(userId);
            setShowDeleteDialog(false);
            navigate('/admin/users', { replace: true });
        } catch {
            // Error is shown via toast in onError callback
            // Dialog stays open so user can try again or cancel
        }
    };

    // Loading state
    if (!isNew && userLoading) {
        return <UserDetailSkeleton />;
    }

    // User not found
    if (!isNew && !user && !userLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-full">
                <UserIcon className="h-12 w-12 text-muted-foreground mb-4" />
                <h2 className="text-lg font-medium">User not found</h2>
                <p className="text-sm text-muted-foreground mt-1">
                    The user you're looking for doesn't exist or you don't have access.
                </p>
                <Button variant="outline" className="mt-4" onClick={() => navigate('/admin/users')}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Users
                </Button>
            </div>
        );
    }

    const displayName = isNew ? 'New User' : getUserDisplayName(user!);
    const initials = isNew ? 'NU' : getUserInitials(user!);
    const avatarUrl = !isNew && user?.avatar
        ? `/api/assets/${user.avatar}?width=200&height=200&fit=cover`
        : undefined;

    const isSaving = createMutation.isPending || updateMutation.isPending;

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="border-b border-border bg-background px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate('/admin/users')}
                        >
                            <ArrowLeft className="h-4 w-4" />
                        </Button>

                        <Avatar className="h-12 w-12">
                            <AvatarImage src={avatarUrl} alt={displayName} />
                            <AvatarFallback className="text-lg font-medium bg-primary/10 text-primary">
                                {initials}
                            </AvatarFallback>
                        </Avatar>

                        <div>
                            <h1 className="text-xl font-semibold text-foreground">
                                {displayName}
                            </h1>
                            {!isNew && user?.email && (
                                <p className="text-sm text-muted-foreground">{user.email}</p>
                            )}
                        </div>

                        {!isNew && user?.status && (
                            <Badge
                                variant="outline"
                                className={cn('capitalize', getStatusColor(user.status))}
                            >
                                {user.status}
                            </Badge>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        {!isNew && (
                            <Button
                                variant="outline"
                                onClick={() => setShowDeleteDialog(true)}
                                className="text-destructive hover:text-destructive"
                            >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                            </Button>
                        )}

                        <Button
                            onClick={handleSave}
                            disabled={isSaving || (!hasChanges && !isNew)}
                        >
                            {isSaving ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="mr-2 h-4 w-4" />
                                    {isNew ? 'Create User' : 'Save Changes'}
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
                <div className="max-w-3xl mx-auto space-y-6">
                    {/* Basic Info */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <UserIcon className="h-5 w-5" />
                                Basic Information
                            </CardTitle>
                            <CardDescription>
                                Personal details and profile information
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="first_name">First Name</Label>
                                    <Input
                                        id="first_name"
                                        value={formData.first_name}
                                        onChange={(e) => handleChange('first_name', e.target.value)}
                                        placeholder="John"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="last_name">Last Name</Label>
                                    <Input
                                        id="last_name"
                                        value={formData.last_name}
                                        onChange={(e) => handleChange('last_name', e.target.value)}
                                        placeholder="Doe"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="title">Title / Role</Label>
                                <Input
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => handleChange('title', e.target.value)}
                                    placeholder="Software Engineer"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="description">Description</Label>
                                <Textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => handleChange('description', e.target.value)}
                                    placeholder="A brief bio or description..."
                                    rows={3}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="location" className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4" />
                                    Location
                                </Label>
                                <Input
                                    id="location"
                                    value={formData.location}
                                    onChange={(e) => handleChange('location', e.target.value)}
                                    placeholder="San Francisco, CA"
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Account */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Mail className="h-5 w-5" />
                                Account
                            </CardTitle>
                            <CardDescription>
                                Email, password, and authentication settings
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">Email *</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleChange('email', e.target.value)}
                                    placeholder="john@example.com"
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password" className="flex items-center gap-2">
                                    <Lock className="h-4 w-4" />
                                    {isNew ? 'Password *' : 'New Password'}
                                </Label>
                                <Input
                                    id="password"
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => handleChange('password', e.target.value)}
                                    placeholder={isNew ? 'Enter password' : 'Leave blank to keep current'}
                                />
                                {!isNew && (
                                    <p className="text-xs text-muted-foreground">
                                        Leave blank to keep the current password
                                    </p>
                                )}
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label htmlFor="email_notifications">Email Notifications</Label>
                                    <p className="text-xs text-muted-foreground">
                                        Receive email notifications about system events
                                    </p>
                                </div>
                                <Switch
                                    id="email_notifications"
                                    checked={formData.email_notifications}
                                    onCheckedChange={(checked) => handleChange('email_notifications', checked)}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Role & Status */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Shield className="h-5 w-5" />
                                Role & Status
                            </CardTitle>
                            <CardDescription>
                                Assign role and control access
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="role">Role</Label>
                                <Select
                                    value={formData.role || '__none__'}
                                    onValueChange={(value) => handleChange('role', value === '__none__' ? '' : value)}
                                    disabled={rolesLoading}
                                >
                                    <SelectTrigger id="role">
                                        <SelectValue placeholder="Select a role" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">
                                            <div className="flex items-center gap-2">
                                                <span className="text-muted-foreground">No role (Public access only)</span>
                                            </div>
                                        </SelectItem>
                                        {roles.map(role => (
                                            <SelectItem key={role.id} value={role.id}>
                                                <div className="flex items-center gap-2">
                                                    <span>{role.name}</span>
                                                    {role.name.toLowerCase().includes('admin') && (
                                                        <Badge variant="destructive" className="text-xs">Admin</Badge>
                                                    )}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">
                                    Users without a role can only access public resources
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="status">Status</Label>
                                <Select
                                    value={formData.status}
                                    onValueChange={(value) => handleChange('status', value as UserStatus)}
                                >
                                    <SelectTrigger id="status">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {statusOptions.map(option => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Delete Dialog */}
            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete User</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete "{displayName}"?
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

// Loading skeleton
function UserDetailSkeleton() {
    return (
        <div className="flex flex-col h-full">
            {/* Header skeleton */}
            <div className="border-b border-border bg-background px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Skeleton className="h-8 w-8 rounded" />
                        <Skeleton className="h-12 w-12 rounded-full" />
                        <div>
                            <Skeleton className="h-6 w-32 mb-1" />
                            <Skeleton className="h-4 w-48" />
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Skeleton className="h-9 w-24" />
                        <Skeleton className="h-9 w-32" />
                    </div>
                </div>
            </div>

            {/* Content skeleton */}
            <div className="flex-1 overflow-auto p-6">
                <div className="max-w-3xl mx-auto space-y-6">
                    {[1, 2, 3].map(i => (
                        <Card key={i}>
                            <CardHeader>
                                <Skeleton className="h-6 w-40 mb-2" />
                                <Skeleton className="h-4 w-64" />
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <Skeleton className="h-10 w-full" />
                                    <Skeleton className="h-10 w-full" />
                                </div>
                                <Skeleton className="h-10 w-full" />
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        </div>
    );
}
